<template>
  <div class="space-y-6">
    <div v-if="catchingStore.loading" class="flex justify-center items-center h-96">
      <div class="spinner w-12 h-12"></div>
    </div>
    <div v-else-if="!catchingStore.currentCatching" class="text-center py-12">
      <p class="text-gray-500">Operation not found</p>
      <router-link to="/operations/catchings" class="text-blue-600">Back to catchings</router-link>
    </div>
    <div v-else class="space-y-6">
      <!-- Header -->
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex justify-between items-start">
          <div>
            <h2 class="text-2xl font-bold">{{ catchingStore.currentCatching.animal_reg_id || 'Operation #' + catchingStore.currentCatching.id }}</h2>
            <p class="text-gray-600 mt-1">
              <span v-if="catchingStore.currentCatching.district" class="mr-3">📍 {{ catchingStore.currentCatching.district.name }}</span>
              <span>📅 {{ new Date(catchingStore.currentCatching.catching_date).toLocaleDateString() }}</span>
            </p>
          </div>
          <span :class="[
            'px-4 py-2 rounded-full font-semibold capitalize',
            catchingStore.currentCatching.status === 'caught' ? 'bg-green-100 text-green-800' :
            catchingStore.currentCatching.status === 'released' ? 'bg-blue-100 text-blue-800' :
            catchingStore.currentCatching.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
          ]">
            {{ statusLabel(catchingStore.currentCatching) }}
          </span>
        </div>
      </div>

      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex flex-wrap items-center gap-2">
          <span class="text-sm text-gray-600">Fitness</span>
          <span :class="fitnessBadgeClass(catchingStore.currentCatching.fitness_status)">
            {{ fitnessLabel(catchingStore.currentCatching.fitness_status) }}
          </span>
        </div>
      </div>

      <!-- Details -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div class="bg-white rounded-lg shadow p-6">
          <h3 class="font-semibold mb-4 text-lg border-b pb-2">🐾 Animal Details</h3>
          <div class="space-y-3 text-sm">
            <div class="flex justify-between">
              <span class="text-gray-600">Registration ID</span>
              <span class="font-medium">{{ catchingStore.currentCatching.animal_reg_id }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Animal Type</span>
              <span class="font-medium capitalize">{{ catchingStore.currentCatching.animal?.name || '—' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Colour</span>
              <span class="font-medium">{{ catchingStore.currentCatching.colour?.name || '—' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Sex</span>
              <span class="font-medium capitalize">{{ catchingStore.currentCatching.sex || '—' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Service</span>
              <span class="font-medium">{{ catchingStore.currentCatching.service?.name || '—' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Date</span>
              <span class="font-medium">{{ new Date(catchingStore.currentCatching.catching_date).toLocaleDateString() }}</span>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-6">
          <h3 class="font-semibold mb-4 text-lg border-b pb-2">📍 Location & Creator</h3>
          <div class="space-y-3 text-sm">
            <div class="flex justify-between">
              <span class="text-gray-600">District</span>
              <span class="font-medium">{{ catchingStore.currentCatching.district?.name || '—' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Ward</span>
              <span class="font-medium">{{ catchingStore.currentCatching.ward }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Area</span>
              <span class="font-medium">{{ catchingStore.currentCatching.area || '—' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Coordinates</span>
              <span class="font-mono text-xs">{{ catchingStore.currentCatching.latitude }}, {{ catchingStore.currentCatching.longitude }}</span>
            </div>
            <div v-if="catchingStore.currentCatching.created_by" class="flex justify-between">
              <span class="text-gray-600">Created By</span>
              <span class="font-medium">{{ catchingStore.currentCatching.created_by.full_name }} ({{ catchingStore.currentCatching.created_by_type }})</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Created At</span>
              <span class="font-medium">{{ catchingStore.currentCatching.created_at ? new Date(catchingStore.currentCatching.created_at).toLocaleString() : '—' }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Remarks -->
      <div class="bg-white rounded-lg shadow p-6">
        <h3 class="font-semibold mb-2">📝 Remarks</h3>
        <p class="text-gray-700">{{ catchingStore.currentCatching.remarks || 'No remarks' }}</p>
      </div>

      <!-- Treatment Workflow -->
      <div v-if="isFitAnimal" class="bg-white rounded-lg shadow overflow-hidden">
        <div class="border-b border-gray-200 px-6 pt-4">
          <div class="flex flex-wrap gap-2">
            <button
              v-for="tab in workflowTabs"
              :key="tab.key"
              @click="activeWorkflowTab = tab.key"
              :class="[
                'px-4 py-2 text-sm font-medium border-b-2',
                activeWorkflowTab === tab.key
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              ]"
            >
              {{ tab.label }}
            </button>
          </div>
        </div>
        <div class="p-6">
          <div v-if="activeWorkflowTab === 'surgery'" class="space-y-4">
            <div>
              <h3 class="font-semibold text-lg">Surgery</h3>
              <p class="text-sm text-gray-600">This animal is marked fit and ready for surgery workflow updates.</p>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div class="rounded-lg bg-gray-50 p-4">
                <div class="text-gray-500">Current Stage</div>
                <div class="font-semibold">Surgery Pending</div>
              </div>
              <div class="rounded-lg bg-gray-50 p-4">
                <div class="text-gray-500">Registration ID</div>
                <div class="font-semibold">{{ catchingStore.currentCatching.animal_reg_id }}</div>
              </div>
              <div class="rounded-lg bg-gray-50 p-4">
                <div class="text-gray-500">District</div>
                <div class="font-semibold">{{ catchingStore.currentCatching.district?.name || '-' }}</div>
              </div>
            </div>
          </div>

          <div v-else-if="activeWorkflowTab === 'post_surgery'" class="space-y-4">
            <div>
              <h3 class="font-semibold text-lg">Post Surgery</h3>
              <p class="text-sm text-gray-600">Use this tab for post surgery monitoring and recovery information.</p>
            </div>
            <div class="rounded-lg border border-dashed border-gray-300 p-6 text-sm text-gray-500">
              Post surgery fields can be added here when the workflow details are finalized.
            </div>
          </div>

          <div v-else class="space-y-4">
            <div>
              <h3 class="font-semibold text-lg">Release</h3>
              <p class="text-sm text-gray-600">Use this tab for release planning after post surgery clearance.</p>
            </div>
            <div class="rounded-lg border border-dashed border-gray-300 p-6 text-sm text-gray-500">
              Release date, location, and approval fields can be added here.
            </div>
          </div>
        </div>
      </div>

      <div v-else class="bg-white rounded-lg shadow p-6">
        <h3 class="font-semibold mb-2">Treatment Workflow</h3>
        <p class="text-sm text-gray-600">
          Mark this animal as fit to show Surgery, Post Surgery, and Release tabs.
        </p>
      </div>

      <!-- Images -->
      <div class="bg-white rounded-lg shadow p-6">
        <h3 class="font-semibold mb-4">📷 Images</h3>
        <div id="images-container" v-if="images.length > 0" class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <div v-for="(image, idx) in images" :key="idx" class="relative group">
            <img
              :src="getImageUrl(image.image_path)"
              :alt="`Image ${idx + 1}`"
              class="w-full h-48 object-cover rounded-lg border cursor-pointer hover:opacity-90"
              @click="openImage(getImageUrl(image.image_path))"
              @error="onImageError($event)"
            />
            <button
              @click="deleteImage(image.id)"
              class="absolute top-2 right-2 p-1 bg-red-600 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
              title="Delete image"
            >
              ✕
            </button>
            <div class="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 rounded-b-lg">
              {{ image.image_type || 'photo' }}
            </div>
          </div>
        </div>
        <p v-else class="text-gray-500">No images uploaded</p>

        <div class="mt-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Upload Images</label>
          <input
            type="file"
            multiple
            accept="image/*"
            @change="handleImageUpload"
            class="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
        </div>
      </div>

      <!-- Update Status -->
      <div class="bg-white rounded-lg shadow p-6">
        <h3 class="font-semibold mb-4">Update Status</h3>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
          <select
            v-model="newStatus"
            class="px-4 py-2 border border-gray-300 rounded"
          >
            <option v-if="activeCatchingStatuses.length === 0" value="" disabled>No statuses configured</option>
            <option v-for="status in activeCatchingStatuses" :key="status.id" :value="status.id">
              {{ status.name }}
            </option>
          </select>
          <select
            v-model="newFitnessStatus"
            class="px-4 py-2 border border-gray-300 rounded"
          >
            <option value="pending">Fitness Pending</option>
            <option value="fit">Fit</option>
            <option value="unfit">Unfit</option>
          </select>
          <div v-if="newFitnessStatus === 'unfit'" class="md:col-span-2">
            <label class="block text-sm font-medium text-gray-700 mb-1">Reason for Unfit *</label>
            <textarea
              v-model="newUnfitReason"
              rows="3"
              placeholder="Enter reason"
              class="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-600"
            ></textarea>
          </div>
          <div v-if="catchingStore.currentCatching.unfit_reason" class="md:col-span-2 text-sm text-gray-600">
            <span class="font-medium">Current Unfit Reason:</span>
            {{ catchingStore.currentCatching.unfit_reason }}
          </div>
          <button
            @click="updateStatus"
            class="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 md:col-span-2"
          >
            Update
          </button>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex gap-2 justify-between">
        <router-link to="/operations/catchings" class="px-6 py-2 border border-gray-300 rounded hover:bg-gray-50">
          ← Back
        </router-link>
        <div class="flex gap-2">
          <router-link
            :to="`/operations/catchings/${route.params.id}/edit`"
            class="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Edit
          </router-link>
          <button
            @click="deleteCatching"
            class="px-6 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Swal from 'sweetalert2'
import { useCatchingStore } from '@/stores/catchingStore'
import { useMasterDataStore } from '@/stores/masterDataStore'

const route = useRoute()
const router = useRouter()
const catchingStore = useCatchingStore()
const masterDataStore = useMasterDataStore()
const images = ref([])
const newStatus = ref('')
const newFitnessStatus = ref('pending')
const newUnfitReason = ref('')
const activeWorkflowTab = ref('surgery')

const workflowTabs = [
  { key: 'surgery', label: 'Surgery' },
  { key: 'post_surgery', label: 'Post Surgery' },
  { key: 'release', label: 'Release' },
]

const isFitAnimal = computed(() => catchingStore.currentCatching?.fitness_status === 'fit')

const activeCatchingStatuses = computed(() => {
  return masterDataStore.statuses
    .filter(status => String(status.is_active) !== '0')
    .map(status => ({
      ...status,
      id: String(status.id || '').trim(),
      code: String(status.code || '').trim(),
      name: status.name || status.code,
      category: String(status.category || '').trim().toLowerCase(),
    }))
    .filter(status => status.id && status.code)
})

const statusValue = (catching) => {
  const rawStatus = String(catching?.status || '').trim()
  if (rawStatus) {
    const status = activeCatchingStatuses.value.find(item => item.id === rawStatus || item.code === rawStatus)
    return status?.id || rawStatus
  }
  const masterStatusId = String(catching?.status_master?.id || '').trim()
  if (masterStatusId) return masterStatusId
  const statusId = String(catching?.status_id || '').trim()
  return statusId || ''
}

const statusLabel = (catchingOrValue) => {
  const value = typeof catchingOrValue === 'object' ? statusValue(catchingOrValue) : catchingOrValue
  const stringValue = String(value || '').trim()
  const status = activeCatchingStatuses.value.find(item => item.id === stringValue || item.code === stringValue)
  return status?.name || (typeof catchingOrValue === 'object' ? catchingOrValue?.status_master?.name || catchingOrValue?.status : stringValue) || 'Pending'
}

const fitnessLabel = (status) => {
  const labels = {
    fit: 'Fit',
    unfit: 'Unfit',
    pending: 'Pending',
  }
  return labels[status] || 'Pending'
}

const fitnessBadgeClass = (status) => {
  const base = 'inline-flex px-3 py-1 rounded-full text-xs font-semibold'
  if (status === 'fit') return `${base} bg-emerald-100 text-emerald-800`
  if (status === 'unfit') return `${base} bg-red-100 text-red-800`
  return `${base} bg-yellow-100 text-yellow-800`
}

// Build full image URL from relative path stored in DB
const getImageUrl = (imagePath) => {
  if (!imagePath) return ''
  // Already absolute URL
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) return imagePath
  // Build from API base URL — strip "/api" to get site root
  const apiUrl = import.meta.env.VITE_API_URL || ''
  const siteRoot = apiUrl.replace(/\/api\/?$/, '')
  // Ensure no double slashes
  const cleanPath = imagePath.startsWith('/') ? imagePath : '/' + imagePath
  return siteRoot + cleanPath
}

const openImage = (url) => {
  window.open(url, '_blank')
}

const onImageError = (event) => {
  event.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" fill="%23ccc"><rect width="200" height="200" fill="%23f0f0f0"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" font-size="14" fill="%23999">Image not found</text></svg>'
}

const deleteImage = async (imageId) => {
  await catchingStore.deleteImage(route.params.id, imageId)
  await loadImages()
}

const deleteCatching = async () => {
  Swal.fire({
    title: 'Confirm Delete',
    text: 'Are you sure you want to delete this catching operation?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Yes, delete it',
    cancelButtonText: 'Cancel'
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        await catchingStore.deleteCatching(route.params.id)
        Swal.fire({
          icon: 'success',
          title: 'Deleted',
          text: 'Catching operation deleted successfully',
          confirmButtonColor: '#3085d6'
        }).then(() => {
          router.push('/operations/catchings')
        })
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Delete Failed',
          text: error.response?.data?.message || catchingStore.error || 'Failed to delete catching operation',
          confirmButtonColor: '#3085d6'
        })
      }
    }
  })
}

const handleImageUpload = async (event) => {
  const files = event.target.files
  if (files.length === 0) return

  const formData = new FormData()
  for (const file of files) {
    formData.append('images', file)
  }
  formData.append('image_type', 'other')

  await catchingStore.uploadImages(route.params.id, formData)
  await loadImages()
}

const updateStatus = async () => {
  if (!newStatus.value || !newFitnessStatus.value) return
  if (newFitnessStatus.value === 'unfit' && !newUnfitReason.value.trim()) {
    Swal.fire({
      icon: 'warning',
      title: 'Reason Required',
      text: 'Please enter a reason when marking the animal unfit.',
      confirmButtonColor: '#3085d6',
    })
    return
  }

  await catchingStore.updateCatching(route.params.id, {
    status: newStatus.value,
    fitness_status: newFitnessStatus.value,
    unfit_reason: newFitnessStatus.value === 'unfit' ? newUnfitReason.value.trim() : '',
  })
  newUnfitReason.value = catchingStore.currentCatching?.unfit_reason || ''
}

const loadImages = async () => {
  images.value = await catchingStore.getImages(route.params.id) || []
}

onMounted(async () => {
  await masterDataStore.getStatuses()
  await catchingStore.getCatching(route.params.id)
  newStatus.value = statusValue(catchingStore.currentCatching)
  newFitnessStatus.value = catchingStore.currentCatching?.fitness_status || 'pending'
  newUnfitReason.value = catchingStore.currentCatching?.unfit_reason || ''
  await loadImages()
})
</script>
